import { Application, Container, Graphics } from "pixi.js";
import { ActDirector } from "./acts/ActDirector";
import { MusicDirector } from "./audio/MusicDirector";
import { Spawner } from "./enemies/Spawner";
import { FakeFifo, FOUNDING_SEEDS, type HelperMessage } from "./help/FakeFifo";
import { ThumbSteer } from "./input/ThumbSteer";
import { RunState } from "./state/RunState";
import { circlesOverlap } from "./systems/Collision";

type Bullet = {
  g: Graphics;
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  fromPlayer: boolean;
  dead: boolean;
};

type Helper = {
  g: Graphics;
  x: number;
  y: number;
  msg: HelperMessage;
  fireCd: number;
};

export type GameCallbacks = {
  onHud: (info: { spares: number; actName: string; progress: number }) => void;
  onHelpOffer: () => void;
  onHelperMessage: (msg: HelperMessage) => void;
  onClear: (result: { elapsed: number; helped: boolean; deaths: number }) => void;
  onDeathForHelp: () => void;
};

export class GameApp {
  app: Application | null = null;
  private root = new Container();
  private bulletLayer = new Container();
  private shipG = new Graphics();
  private hurtboxG = new Graphics();
  private steer = new ThumbSteer({ width: 390, height: 700, margin: 22 });
  private ship = { x: 195, y: 500 };
  private bullets: Bullet[] = [];
  private helpers: Helper[] = [];
  private run = new RunState(3);
  private acts = new ActDirector();
  private music = new MusicDirector(390);
  private spawner = new Spawner(390, 700);
  private fireCd = 0;
  private running = false;
  private pausedForHelp = false;
  private helpOffers = 0;
  private w = 390;
  private h = 700;
  private fifo = new FakeFifo(FOUNDING_SEEDS);
  private starfield = new Graphics();

  constructor(
    private host: HTMLElement,
    private cb: GameCallbacks,
  ) {}

  async init(): Promise<void> {
    const app = new Application();
    await app.init({
      resizeTo: this.host,
      background: "#050508",
      antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
    });
    this.app = app;
    this.host.appendChild(app.canvas);
    app.stage.addChild(this.root);
    this.root.addChild(this.starfield);
    this.root.addChild(this.bulletLayer);
    this.root.addChild(this.shipG);
    this.root.addChild(this.hurtboxG);

    this.layout();
    this.drawShip();
    this.drawStars();
    this.bindInput(app.canvas);

    app.ticker.add((t) => {
      const dt = t.deltaMS / 1000;
      this.update(dt);
    });

    await this.music.load("/audio/main.ogg");
  }

  private layout(): void {
    if (!this.app) return;
    this.w = this.app.screen.width;
    this.h = this.app.screen.height;
    this.steer.setField({ width: this.w, height: this.h, margin: 22 });
    this.spawner = new Spawner(this.w, this.h);
    this.ship.x = this.w / 2;
    this.ship.y = this.h * 0.72;
    this.steer.center();
  }

  private bindInput(canvas: HTMLCanvasElement): void {
    const toLocal = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: ((e.clientX - rect.left) / rect.width) * this.w,
        y: ((e.clientY - rect.top) / rect.height) * this.h,
      };
    };

    canvas.addEventListener("pointerdown", (e) => {
      if (!this.running || this.pausedForHelp) return;
      canvas.setPointerCapture(e.pointerId);
      const p = toLocal(e);
      this.steer.onPointerDown(e.pointerId, p.x, p.y);
    });
    canvas.addEventListener("pointermove", (e) => {
      if (!this.running || this.pausedForHelp) return;
      const p = toLocal(e);
      this.steer.onPointerMove(e.pointerId, p.x, p.y);
    });
    const up = (e: PointerEvent) => this.steer.onPointerUp(e.pointerId);
    canvas.addEventListener("pointerup", up);
    canvas.addEventListener("pointercancel", up);
  }

  async startRun(): Promise<void> {
    this.resetWorld();
    this.running = true;
    this.pausedForHelp = false;
    await this.music.play();
  }

  stopRun(): void {
    this.running = false;
    this.music.stop();
  }

  private resetWorld(): void {
    for (const b of this.bullets) b.g.destroy();
    this.bullets = [];
    this.bulletLayer.removeChildren();
    for (const h of this.helpers) h.g.destroy();
    this.helpers = [];
    this.run = new RunState(3);
    this.acts = new ActDirector();
    this.helpOffers = 0;
    this.fireCd = 0;
    this.ship.x = this.w / 2;
    this.ship.y = this.h * 0.72;
    this.steer.center();
    this.drawShip();
  }

  /** How many helpers the next Accept should pull (escalates 1→3). */
  helpPullCount(): number {
    return Math.min(1 + this.helpOffers, 3);
  }

  /**
   * Called when player Accepts help.
   * Pass `remote` messages from GET /help/next when API is live; otherwise local FIFO ring.
   */
  acceptHelp(remote?: HelperMessage[]): void {
    this.pausedForHelp = false;
    this.run.alive = true;
    if (this.run.spares < 0) this.run.spares = 0;
    this.run.invuln = 2;
    this.run.flags.acceptedHelp = true;

    const n = this.helpPullCount();
    const msgs =
      remote && remote.length > 0 ? remote.slice(0, n) : this.fifo.next(n);
    this.helpOffers += 1;

    msgs.forEach((msg, i) => {
      this.spawnHelper(msg, i, msgs.length);
      this.cb.onHelperMessage(msg);
    });
    this.running = true;
  }

  refuseHelp(): void {
    this.pausedForHelp = false;
    this.run.alive = true;
    this.run.spares = Math.max(0, this.run.spares);
    this.run.invuln = 2;
    this.running = true;
  }

  private spawnHelper(msg: HelperMessage, index: number, total: number): void {
    const g = new Graphics();
    g.circle(0, 0, 10);
    g.fill({ color: 0x7ec8d4, alpha: 0.9 });
    g.circle(0, 0, 16);
    g.stroke({ width: 1, color: 0x7ec8d4, alpha: 0.5 });
    this.root.addChild(g);
    const spread = (index - (total - 1) / 2) * 36;
    this.helpers.push({
      g,
      x: this.ship.x + spread,
      y: this.ship.y - 48,
      msg,
      fireCd: 0.1 * index,
    });
    this.run.flags.helpersJoined += 1;
  }

  private update(dt: number): void {
    if (!this.running || this.pausedForHelp) return;

    this.music.tick(dt);
    this.run.elapsed += dt;
    this.run.tickInvuln(dt);
    this.acts.setProgress(this.music.progress);

    this.ship = this.steer.step(this.ship, dt);
    this.drawShip();

    // Player auto-fire up
    this.fireCd -= dt;
    if (this.fireCd <= 0) {
      this.fireCd = 0.09;
      this.spawnBullet(this.ship.x, this.ship.y - 18, 0, -520, true, 3);
    }

    // Helpers
    for (const h of this.helpers) {
      h.x += (this.ship.x - h.x) * (1 - Math.exp(-4 * dt));
      h.y += (this.ship.y - 52 - h.y) * (1 - Math.exp(-4 * dt));
      h.g.position.set(h.x, h.y);
      h.fireCd -= dt;
      if (h.fireCd <= 0) {
        h.fireCd = 0.12;
        this.spawnBullet(h.x, h.y - 10, 0, -480, true, 2.5);
      }
    }

    // Enemy patterns
    if (this.run.alive) {
      const events = this.spawner.update(
        dt,
        this.acts,
        this.ship.x,
        this.ship.y,
      );
      for (const e of events) {
        this.spawnBullet(e.x, e.y, e.vx, e.vy, false, e.r ?? 4);
      }
    }

    // Integrate bullets
    for (const b of this.bullets) {
      if (b.dead) continue;
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.g.position.set(b.x, b.y);
      if (b.x < -40 || b.x > this.w + 40 || b.y < -40 || b.y > this.h + 40) {
        b.dead = true;
      }
    }

    // Collisions
    const hurt: { x: number; y: number; r: number } = {
      x: this.ship.x,
      y: this.ship.y,
      r: 5,
    };
    for (const b of this.bullets) {
      if (b.dead || b.fromPlayer) continue;
      if (this.run.invuln > 0) continue;
      if (circlesOverlap(hurt, { x: b.x, y: b.y, r: b.r })) {
        b.dead = true;
        const result = this.run.hit();
        if (result === "dead") {
          this.handleDeath();
          break;
        }
      }
    }

    // GC bullets
    if (this.bullets.length > 400) {
      const live: Bullet[] = [];
      for (const b of this.bullets) {
        if (b.dead) b.g.destroy();
        else live.push(b);
      }
      this.bullets = live;
    }

    this.cb.onHud({
      spares: Math.max(0, this.run.spares),
      actName: this.acts.current.name,
      progress: this.music.progress,
    });

    if (this.acts.finished && this.run.alive) {
      this.run.markClear();
      this.running = false;
      this.music.stop();
      this.cb.onClear({
        elapsed: this.run.elapsed,
        helped: this.run.flags.acceptedHelp,
        deaths: this.run.flags.deaths,
      });
    }
  }

  private handleDeath(): void {
    this.running = false;
    // Offer help Automata-style after deaths once into Weight+
    const shouldOffer =
      this.acts.current.id >= 2 || this.run.flags.deaths >= 1;
    if (shouldOffer) {
      this.pausedForHelp = true;
      this.cb.onDeathForHelp();
      this.cb.onHelpOffer();
    } else {
      // Early soft death: brief restart with spare reset-ish
      this.run.alive = true;
      this.run.spares = 2;
      this.run.invuln = 2;
      this.running = true;
    }
  }

  private spawnBullet(
    x: number,
    y: number,
    vx: number,
    vy: number,
    fromPlayer: boolean,
    r: number,
  ): void {
    const g = new Graphics();
    if (fromPlayer) {
      g.rect(-1.5, -6, 3, 12);
      g.fill(0xe8e6e1);
    } else {
      g.circle(0, 0, r);
      g.fill(0xc45c5c);
    }
    g.position.set(x, y);
    this.bulletLayer.addChild(g);
    this.bullets.push({ g, x, y, vx, vy, r, fromPlayer, dead: false });
  }

  private drawShip(): void {
    const g = this.shipG;
    g.clear();
    const flash = this.run.invuln > 0 && Math.floor(this.run.invuln * 20) % 2 === 0;
    const col = flash ? 0x7ec8d4 : 0xe8e6e1;
    // Simple craft silhouette (placeholder museum mesh)
    g.moveTo(0, -14);
    g.lineTo(10, 10);
    g.lineTo(0, 6);
    g.lineTo(-10, 10);
    g.closePath();
    g.fill({ color: col, alpha: 0.95 });
    g.position.set(this.ship.x, this.ship.y);

    this.hurtboxG.clear();
    this.hurtboxG.circle(0, 0, 5);
    this.hurtboxG.stroke({ width: 1, color: 0x7ec8d4, alpha: 0.35 });
    this.hurtboxG.position.set(this.ship.x, this.ship.y);
  }

  private drawStars(): void {
    this.starfield.clear();
    for (let i = 0; i < 60; i++) {
      const x = Math.random() * 800;
      const y = Math.random() * 1200;
      const a = 0.15 + Math.random() * 0.4;
      this.starfield.rect(x, y, 1, 1);
      this.starfield.fill({ color: 0xffffff, alpha: a });
    }
  }

  destroy(): void {
    this.stopRun();
    this.app?.destroy(true);
    this.app = null;
  }
}
